import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { toast } from 'sonner';
import { Frame, ScoutErrorType, ScoutLocation } from '@/types/location';
import { filterInvalidHeadings, snapToLine, stitch } from '@/utils/imagery';
import { sortSequencesByTimestamp } from '@/utils/sort';
import { getImagesForPolygon } from '@/api/developer';
import { preventScroll } from '@/utils/keyboard';
import CarouselSkeleton from '@/components/ui/scout/skeleton/CarouselSkeleton';
import Carousel from '@/components/ui/scout/imagery/Carousel';
import Thumbnail from '@/components/ui/scout/imagery/Thumbnail';
import ImagerySkeleton from '@/components/ui/scout/skeleton/ImagerySkeleton';
import * as Toast from '@/components/ui/toast/Toast';
import { useMapCredits } from '@/hooks/useMapCredits';
import { useScout } from '@/hooks/useScout';
import { isLatestBurstActive } from '@/utils/scout';
import { useUser } from '@/hooks/useUser';
import { Views } from '@/types/view';

interface Props {
  location: ScoutLocation;
  sortedSequences: Frame[][];
  activeView: Views;
  setSortedSequences: Dispatch<SetStateAction<Frame[][]>>;
  activeSequence: Frame[] | null;
  activeSequenceIndex: number;
  setActiveSequenceIndex: Dispatch<SetStateAction<number>>;
  apiCallsComplete: boolean;
  setApiCallsComplete: Dispatch<SetStateAction<boolean>>;
  activeFrameIndex: { value: number };
  setActiveFrameIndex: Dispatch<SetStateAction<{ value: number }>>;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  setRequestCost: Dispatch<SetStateAction<number>>;
  setBurstModalOptions: Dispatch<
    SetStateAction<{
      isOpen: boolean;
      locationsIds: string[];
      successCallback: () => void;
    }>
  >;
}

const Imagery: React.FC<Props> = ({
  location,
  activeView,
  sortedSequences,
  setSortedSequences,
  activeSequence,
  activeSequenceIndex,
  setActiveSequenceIndex,
  apiCallsComplete,
  setApiCallsComplete,
  activeFrameIndex,
  setActiveFrameIndex,
  setShowModal,
  setRequestCost,
  setBurstModalOptions,
}) => {
  const [errorReason, setErrorReason] = useState<ScoutErrorType>();
  const [addedToDemo, setAddedToDemo] = useState<string[]>(
    location.jobIds || [],
  );

  const { setLocations } = useScout();
  const { setMapCredits, setCreditsModalOptions } = useMapCredits();
  const { user } = useUser();

  const divRef: RefObject<HTMLDivElement> = useRef(null);
  const thumbnailRefs = useRef<Array<RefObject<HTMLDivElement>>>([]);

  const hasActiveBurst = isLatestBurstActive(location.bursts);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.01,
  });

  const imageryCallback = useCallback(
    (id: string) => {
      setLocations(prevState => {
        return prevState.map(location => {
          if (location._id === id) {
            return {
              ...location,
              lastViewed: new Date().toISOString(),
            };
          }

          return location;
        });
      });
    },
    [setLocations],
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (!user) return;

    if (inView) {
      const fetchImagery = async () => {
        setRequestCost(0);
        setErrorReason(undefined);

        const data = await getImagesForPolygon(
          location._id,
          location.geojson,
          user.currentOrganization,
          signal,
          imageryCallback,
        );

        if ('error' in data) {
          if (!signal.aborted) {
            toast.custom(t => (
              <Toast.Default
                title="Request Failed"
                description={data.error.message || data.error}
                buttons={[
                  {
                    label: 'Dismiss',
                    onClick: () => toast.dismiss(t),
                    variant: 'secondary',
                    size: 'fw',
                  },
                ]}
              />
            ));
          }

          setErrorReason('unknown');
          setApiCallsComplete(true);
          setSortedSequences([]);
          return;
        }

        const { frames, cost, creditsRemaining, notEnoughCredits } = data;
        setMapCredits(creditsRemaining ?? 0);
        let freshestFrameTimestamp = 0;

        if (frames.length > 0) {
          const stitched = stitch(frames);
          let sorted = sortSequencesByTimestamp(stitched);

          if (location.segments) {
            const filtered = filterInvalidHeadings(sorted, location.segments);
            sorted = snapToLine(filtered, location.segments);
          }

          setSortedSequences(sorted);

          if (sorted.length > 0) {
            const freshestSequence = sorted[0];
            const freshestFrame = freshestSequence[freshestSequence.length - 1];
            freshestFrameTimestamp = new Date(
              freshestFrame.timestamp,
            ).getTime();
          } else {
            setErrorReason('imagery');
          }
        } else {
          setErrorReason('imagery');
        }

        if (location.lastSeen) {
          const locationLastSeenTimestamp = new Date(
            location.lastSeen,
          ).getTime();

          if (locationLastSeenTimestamp > freshestFrameTimestamp) {
            toast.custom(t => (
              <Toast.Default
                title="Check Back Soon"
                description="We're still processing the most recent imagery at this location. We appreciate your patience."
                buttons={[
                  {
                    label: 'Dismiss',
                    onClick: () => toast.dismiss(t),
                    variant: 'secondary',
                    size: 'fw',
                  },
                ]}
              />
            ));
          }
        }

        if (notEnoughCredits) {
          setErrorReason('mapCredits');
          toast.custom(t => (
            <Toast.Default
              title="Map Credits Needed"
              description="Purchase map credits to continue using Scout."
              buttons={[
                {
                  label: 'Dismiss',
                  onClick: () => toast.dismiss(t),
                  variant: 'secondary',
                  size: 'fw',
                },
                {
                  label: 'Purchase Credits',
                  onClick: () => {
                    setCreditsModalOptions({ isVisible: true });
                    toast.dismiss(t);
                  },
                  variant: 'default',
                  size: 'fw',
                },
              ]}
            />
          ));
        }

        setRequestCost(cost);
        setApiCallsComplete(true);
      };

      fetchImagery();
    }

    return () => {
      controller.abort();
    };
  }, [
    inView,
    setApiCallsComplete,
    location._id,
    location.geojson,
    location.segments,
    location.lastSeen,
    user,
    setActiveFrameIndex,
    setActiveSequenceIndex,
    setRequestCost,
    setSortedSequences,
    setMapCredits,
    imageryCallback,
    setCreditsModalOptions,
  ]);

  useEffect(() => {
    const divElement = divRef.current;
    if (!divElement) return;

    const addPreventScroll = () => {
      window.addEventListener('keydown', preventScroll);
    };

    const removePreventScroll = () => {
      window.removeEventListener('keydown', preventScroll);
    };

    divElement.addEventListener('focus', addPreventScroll);
    divElement.addEventListener('blur', removePreventScroll);

    return () => {
      divElement.removeEventListener('focus', addPreventScroll);
      divElement.removeEventListener('blur', removePreventScroll);
    };
  }, []);

  const scrollTo = (activeSequenceIndex: number) => {
    if (thumbnailRefs.current.length < 1) return;
    if (thumbnailRefs.current[activeSequenceIndex].current) {
      thumbnailRefs.current[activeSequenceIndex].current!.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  };

  useEffect(() => {
    if (apiCallsComplete) {
      scrollTo(activeSequenceIndex);
    }
  }, [activeSequenceIndex, apiCallsComplete]);

  return (
    <div ref={ref} className="flex h-auto w-full flex-col">
      {sortedSequences && activeSequence ? (
        <Carousel
          locationId={location._id}
          addedToDemo={addedToDemo}
          setAddedToDemo={setAddedToDemo}
          sortedSequences={sortedSequences}
          activeSequence={activeSequence}
          activeView={activeView}
          activeLocation={location}
          activeSequenceIndex={activeSequenceIndex}
          setActiveSequenceIndex={setActiveSequenceIndex}
          activeFrameIndex={activeFrameIndex}
          setActiveFrameIndex={setActiveFrameIndex}
          setShowModal={setShowModal}
        />
      ) : (
        <CarouselSkeleton
          apiCallsComplete={apiCallsComplete}
          errorReason={errorReason}
        />
      )}
      {sortedSequences.length > 0 ? (
        <div
          ref={divRef}
          className="no-scrollbar mt-3 flex flex-row overflow-x-scroll focus:outline-none"
          tabIndex={0}>
          {sortedSequences.map((sequence, index) => {
            const ref = React.createRef<HTMLDivElement>();
            thumbnailRefs.current[index] = ref;

            const isLast = index === sortedSequences.length - 1;
            return (
              <div
                data-last={isLast}
                key={sequence[0].url}
                ref={ref}
                className="mr-2 min-w-[30%] data-[last=true]:mr-2">
                <Thumbnail
                  key={sequence[0].url}
                  url={sequence[0].url}
                  timestamp={sequence[0].timestamp}
                  sequence={sequence[0].sequence}
                  onClick={() => {
                    setActiveSequenceIndex(index);
                    setActiveFrameIndex({ value: 0 });
                  }}
                  isActive={index === activeSequenceIndex}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <ImagerySkeleton
          apiCallsComplete={apiCallsComplete}
          errorReason={errorReason}
          id={location._id}
          setBurstModalOptions={setBurstModalOptions}
          hasActiveBurst={hasActiveBurst}
        />
      )}
    </div>
  );
};

export default Imagery;
